import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { can } from "@/lib/permissions";

const DONE_STATUSES = ["Complete", "Not Applicable"];
const isDone = (s) => DONE_STATUSES.includes(s);

function lotProgress(lot) {
  const items = lot.checklist_items || [];
  if (!items.length) return 0;
  return Math.round((items.filter(i => isDone(i.status)).length / items.length) * 100);
}

function pointCounts(lot) {
  const hps = lot.hold_points || [];
  const aps = lot.approval_points || [];
  const wps = (lot.checklist_items || []).filter(i => (i.linked_hold_point || "").toUpperCase().startsWith("WP"));
  return {
    hp: { done: hps.filter(h => h.status === "Approved / Released").length, total: hps.length },
    ap: { done: aps.filter(a => ["Approved / Released", "Approved"].includes(a.status)).length, total: aps.length },
    wp: { done: wps.filter(i => isDone(i.status)).length, total: wps.length },
  };
}

function PointPill({ label, done, total }) {
  if (!total) return null;
  const complete = done === total;
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[11px] font-semibold ${complete ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
      {label} {done}/{total}
    </span>
  );
}

function ProgressBar({ pct }) {
  const color = pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-amber-500" : "bg-slate-300";
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 text-right font-mono text-xs font-semibold text-slate-700">{pct}%</span>
    </div>
  );
}

function FilterPill({ label, value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-auto gap-1.5 rounded-full border-slate-300 bg-white px-3 text-xs font-medium" data-testid={testId}>
        <span className="text-slate-500">{label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function LotRegister() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [lots, setLots] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [contractorFilter, setContractorFilter] = useState("all");
  const [activityFilter, setActivityFilter] = useState("all");

  useEffect(() => {
    api.get("/lots").then(r => setLots(r.data));
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const userById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const contractorName = (lot) => {
    const u = userById[lot.subcontractor_id];
    return u ? (u.company || u.name) : null;
  };

  const contractorOptions = useMemo(() => {
    const set = new Map();
    lots.forEach(l => { const n = contractorName(l); if (n) set.set(l.subcontractor_id, n); });
    return [...set.entries()].map(([value, label]) => ({ value, label }));
  }, [lots, userById]);

  const activityOptions = useMemo(() => {
    const set = new Set(lots.map(l => l.activity).filter(Boolean));
    return [...set].sort().map(a => ({ value: a, label: a }));
  }, [lots]);

  const filtered = useMemo(() => lots.filter(l =>
    (statusFilter === "all" || l.status === statusFilter) &&
    (contractorFilter === "all" || l.subcontractor_id === contractorFilter) &&
    (activityFilter === "all" || l.activity === activityFilter)
  ), [lots, statusFilter, contractorFilter, activityFilter]);

  const openLot = (lotId) => nav(`/lots/${lotId}`);

  return (
    <div className="p-4 md:p-8" data-testid="lots-register-page">
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-1">Register</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
            Lot register
            <span className="ml-3 align-middle font-sans text-sm font-medium tracking-normal text-slate-500" data-testid="lots-count">
              • {filtered.length} lot{filtered.length === 1 ? "" : "s"}
            </span>
          </h1>
        </div>
        <Link to={`/lots/new${typeof window !== "undefined" && window.location.search.includes("itp=") ? window.location.search : ""}`}>
          {can.createLot(user?.role) && (
            <Button className="bg-teal-700 hover:bg-teal-800" data-testid="create-lot-btn">
              <Plus className="mr-2 size-4" />Create Lot
            </Button>
          )}
        </Link>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterPill label="Contractor" value={contractorFilter} onChange={setContractorFilter} options={contractorOptions} testId="lots-filter-contractor" />
        <FilterPill label="Activity" value={activityFilter} onChange={setActivityFilter} options={activityOptions} testId="lots-filter-activity" />
        <FilterPill
          label="Status" value={statusFilter} onChange={setStatusFilter} testId="lots-filter"
          options={["Draft","Open","In Progress","Hold Point Pending","Hold Point Rejected","Ready for Review","Closed","Rejected"].map(s => ({ value: s, label: s }))}
        />
      </div>

      <Card className="overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Lot</th>
                <th className="px-4 py-3">Activity</th>
                <th className="px-4 py-3">Chainage</th>
                <th className="px-4 py-3">ITP</th>
                <th className="px-4 py-3">Hold / Witness</th>
                <th className="px-4 py-3">Complete</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lot => {
                const pts = pointCounts(lot);
                const pct = lotProgress(lot);
                const noPoints = !pts.hp.total && !pts.wp.total && !pts.ap.total;
                return (
                  <tr
                    key={lot.id}
                    className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
                    onClick={() => openLot(lot.id)}
                    data-testid={`lot-row-${lot.lot_number}`}
                  >
                    <td className="px-4 py-3 font-mono font-semibold">
                      <Link
                        to={`/lots/${lot.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-teal-700 hover:underline"
                        data-testid={`lot-link-${lot.lot_number}`}
                      >
                        {lot.lot_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{lot.activity || "—"}</div>
                      {contractorName(lot) && <div className="text-xs text-slate-500">{contractorName(lot)}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{lot.location || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{lot.itp_number}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1" data-testid={`lot-points-${lot.lot_number}`}>
                        <PointPill label="HP" done={pts.hp.done} total={pts.hp.total} />
                        <PointPill label="WP" done={pts.wp.done} total={pts.wp.total} />
                        <PointPill label="AP" done={pts.ap.done} total={pts.ap.total} />
                        {noPoints && <span className="text-xs text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-testid={`lot-progress-${lot.lot_number}`}><ProgressBar pct={pct} /></td>
                    <td className="px-4 py-3"><StatusBadge status={lot.status} /></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No lots</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export function CreateLot() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [itps, setItps] = useState([]);
  const [users, setUsers] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [preview, setPreview] = useState("");
  const prefilledItp = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("itp") || "" : "";
  const [form, setForm] = useState({
    lot_number: "", project_id: "", activity: "", itp_template_id: prefilledItp,
    location: "", drawing_number: "", specification_reference: "",
    responsible_engineer_id: "", subcontractor_id: "", planned_inspection_date: "",
    client_itp_reference_number: "", client_lot_number: "",
    series_id: "", manual_override: false,
  });
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/itp-templates").then(r => setItps(r.data.filter(t => t.status === "Approved")));
    api.get("/users").then(r => setUsers(r.data));
  }, []);

  // Reload series when project changes
  useEffect(() => {
    if (!form.project_id) { setSeriesList([]); return; }
    api.get(`/projects/${form.project_id}/numbering-series`).then(r => {
      const active = (r.data || []).filter(s => s.active);
      setSeriesList(active);
      // Default to project default series if any and not yet chosen
      if (!form.series_id) {
        const def = active.find(s => s.is_default);
        if (def) setForm(f => ({ ...f, series_id: def.id }));
      }
    }).catch(() => setSeriesList([]));
  }, [form.project_id]);

  // Preview number when series changes
  useEffect(() => {
    if (!form.series_id || form.series_id === "__none__") { setPreview(""); return; }
    api.get(`/numbering-series/${form.series_id}/preview`).then(r => setPreview(r.data.next_number)).catch(() => setPreview(""));
  }, [form.series_id]);

  const create = async () => {
    if (!form.project_id || !form.itp_template_id) {
      toast.error("Project and ITP are required");
      return;
    }
    const seriesId = form.series_id && form.series_id !== "__none__" ? form.series_id : "";
    const usingSeries = !!seriesId;
    if (usingSeries && form.manual_override && !form.lot_number) {
      toast.error("Manual override requires a lot number");
      return;
    }
    if (!usingSeries && !form.lot_number) {
      toast.error("Lot number is required when no numbering series is selected");
      return;
    }
    try {
      const body = {
        ...form,
        responsible_engineer_id: form.responsible_engineer_id || null,
        subcontractor_id: form.subcontractor_id || null,
        planned_inspection_date: form.planned_inspection_date || null,
        series_id: seriesId || null,
        lot_number: usingSeries && !form.manual_override ? null : form.lot_number,
      };
      const { data } = await api.post("/lots", body);
      toast.success(`Lot ${data.lot_number} created`);
      nav(`/lots/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="create-lot-page">
      <Link to="/lots" className="text-sm text-slate-500 hover:underline">← Lots</Link>
      <div className="mt-3 mb-6">
        <div className="overline mb-1">New Lot</div>
        <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Create Lot</h1>
        <p className="mt-1 text-sm text-slate-600">Open a new inspection lot. Hold points are generated automatically from the chosen ITP.</p>
      </div>

      <Card className="border-slate-200 p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2 rounded-md border border-teal-200 bg-teal-50/40 p-3" data-testid="lot-numbering-section">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Lot Numbering</Label>
              {form.project_id && (
                <Link to={`/projects/${form.project_id}/numbering-series`} className="text-xs text-teal-700 hover:underline" data-testid="manage-series-link">Manage series →</Link>
              )}
            </div>
            {!form.project_id && (
              <p className="mt-1 text-xs text-slate-600">Select a project first to see its numbering series.</p>
            )}
            {form.project_id && seriesList.length === 0 && (
              <p className="mt-1 text-xs text-slate-600">No numbering series exists for this project. Enter a lot number manually below or <Link to={`/projects/${form.project_id}/numbering-series`} className="text-teal-700 underline">create a series</Link>.</p>
            )}
            {form.project_id && seriesList.length > 0 && (
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="md:col-span-2">
                  <Label className="text-xs">Numbering Series</Label>
                  <Select value={form.series_id} onValueChange={(v) => setForm({ ...form, series_id: v, manual_override: false, lot_number: "" })}>
                    <SelectTrigger className="h-10" data-testid="lot-series-select"><SelectValue placeholder="Select series (or leave blank for manual)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" data-testid="lot-series-none">— Manual (no series) —</SelectItem>
                      {seriesList.map(s => (
                        <SelectItem key={s.id} value={s.id} data-testid={`lot-series-option-${s.id}`}>
                          {s.name} · {s.prefix ? `${s.prefix}${s.separator}${"0".repeat(s.suffix_width)}` : "(no prefix)"}{s.is_default ? " · default" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Next Lot Number</Label>
                  <div className="mt-1 h-10 rounded-md border border-teal-300 bg-white px-3 py-2 font-mono text-sm text-teal-900" data-testid="lot-series-preview">
                    {form.series_id && form.series_id !== "__none__" ? (preview || "…") : "— manual —"}
                  </div>
                </div>
              </div>
            )}
            {isAdmin && form.series_id && form.series_id !== "__none__" && (
              <label className="mt-3 flex items-center gap-2 text-xs text-slate-700" data-testid="lot-manual-override-label">
                <input type="checkbox" checked={form.manual_override} onChange={(e) => setForm({ ...form, manual_override: e.target.checked, lot_number: "" })} data-testid="lot-manual-override-checkbox" />
                <span>Override series with a manual lot number (admin only)</span>
              </label>
            )}
            {(!form.series_id || form.series_id === "__none__" || form.manual_override) && (
              <div className="mt-3">
                <Label className="text-xs">Lot Number *</Label>
                <Input value={form.lot_number} onChange={e => setForm({ ...form, lot_number: e.target.value })} placeholder="TRIU-DR-002" data-testid="lot-number" className="h-10 mt-1 max-w-sm" />
              </div>
            )}
          </div>
          <div>
            <Label>Client ITP Reference Number</Label>
            <Input value={form.client_itp_reference_number} onChange={e=>setForm({...form, client_itp_reference_number:e.target.value})} placeholder="e.g. 1841-T100-WGI-QAC-ITP-1024" data-testid="lot-client-itp-ref" className="h-11"/>
            <p className="mt-1 text-xs text-slate-500">Optional. Used in Close-Out Package naming.</p>
          </div>
          <div>
            <Label>Client Lot Number</Label>
            <Input value={form.client_lot_number} onChange={e=>setForm({...form, client_lot_number:e.target.value})} placeholder="e.g. CL-001" data-testid="lot-client-lot-number" className="h-11"/>
          </div>
          <div>
            <Label>Project *</Label>
            <Select value={form.project_id} onValueChange={v=>setForm({...form, project_id:v, series_id: "", manual_override: false})}>
              <SelectTrigger className="h-11" data-testid="lot-project"><SelectValue placeholder="Select project"/></SelectTrigger>
              <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.number} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>ITP Template *</Label>
            <Select value={form.itp_template_id} onValueChange={v=>setForm({...form, itp_template_id:v})}>
              <SelectTrigger className="h-11" data-testid="lot-itp"><SelectValue placeholder="Select ITP"/></SelectTrigger>
              <SelectContent>{itps.map(t => <SelectItem key={t.id} value={t.id}>{t.itp_number} — {t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Activity</Label>
            <Input value={form.activity} onChange={e=>setForm({...form, activity:e.target.value})} placeholder="Stormwater Pit Installation" data-testid="lot-activity" className="h-11"/>
          </div>
          <div>
            <Label>Location / Chainage</Label>
            <Input value={form.location} onChange={e=>setForm({...form, location:e.target.value})} placeholder="CH 1+200 to 1+250" data-testid="lot-location" className="h-11"/>
          </div>
          <div>
            <Label>Drawing Number</Label>
            <Input value={form.drawing_number} onChange={e=>setForm({...form, drawing_number:e.target.value})} data-testid="lot-drawing" className="h-11"/>
          </div>
          <div>
            <Label>Specification Reference</Label>
            <Input value={form.specification_reference} onChange={e=>setForm({...form, specification_reference:e.target.value})} className="h-11"/>
          </div>
          <div>
            <Label>Responsible Engineer</Label>
            <Select value={form.responsible_engineer_id} onValueChange={v=>setForm({...form, responsible_engineer_id:v})}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Site Engineer"/></SelectTrigger>
              <SelectContent>{users.filter(u=>u.role==="site_engineer").map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Subcontractor</Label>
            <Select value={form.subcontractor_id} onValueChange={v=>setForm({...form, subcontractor_id:v})}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Subcontractor"/></SelectTrigger>
              <SelectContent>{users.filter(u=>u.role==="subcontractor").map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.company}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>ITP Open Date</Label>
            <Input type="date" value={form.planned_inspection_date} onChange={e=>setForm({...form, planned_inspection_date:e.target.value})} className="h-11" data-testid="lot-itp-open-date"/>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => nav("/lots")}>Cancel</Button>
          <Button onClick={create} className="bg-teal-700 hover:bg-teal-800" data-testid="lot-save">Create Lot</Button>
        </div>
      </Card>
    </div>
  );
}
